import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { anomalyReasonText } from '../../utils/anomalySignals';
import { formatCurrency } from '../../utils/formatting';
import {
    getAgingStatusColor,
    getBuyerHeatStatus,
    getAnomalyStyle,
    getRiskTierStyle,
} from '../../utils/unitDetailHelpers';
import InfoTip from '../InfoTip';
import { unitAgingBucketId, unitAgingLabel } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import AiPriceCell, { AiPriceHeaderTip } from './AiPriceCell';
import { AI_GAP_SORT_KEY, DEFAULT_SORT, nextSort, pageNumbers, sortInventory } from '../../utils/inventoryView';
import './InventoryTable.css';

function InventoryTable({ data, onUnitClick, loading, onClearFilters, retargetActive }) {
    const { aging } = useDealershipSettings();
    const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Reset page when data changes
    useEffect(() => { setPage(1); }, [data.length]);

    const sortedData = useMemo(() => sortInventory(data, sortConfig), [data, sortConfig]);

    const totalPages = Math.ceil(sortedData.length / pageSize);
    const paginatedData = sortedData.slice((page - 1) * pageSize, page * pageSize);

    const requestSort = (key, defaultDirection = 'asc') => {
        setSortConfig(nextSort(sortConfig, key, defaultDirection));
        setPage(1); // a new order starts at its top
    };

    // Props that make a <th> sortable by mouse AND keyboard (Enter / Space),
    // and announce the active sort via aria-sort. Keys pressed on a nested
    // control (e.g. a tooltip trigger) are ignored.
    const sortableTh = (key, defaultDirection = 'asc') => ({
        tabIndex: 0,
        'aria-sort': sortConfig.key === key
            ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending')
            : 'none',
        onClick: () => requestSort(key, defaultDirection),
        onKeyDown: (e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                requestSort(key, defaultDirection);
            }
        },
    });

    const getSortIndicator = (name) => {
        if (sortConfig.key !== name) return <span className="sort-icon muted" aria-hidden="true">&#8597;</span>;
        return <span className="sort-icon active" aria-hidden="true">{sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC'}</span>;
    };

    const getPageNumbers = () => pageNumbers(page, totalPages);

    if (loading) {
        return <div className="table-loading"><div className="spinner"></div><p>Loading inventory...</p></div>;
    }

    if (!data || data.length === 0) {
        return (
            <div className="no-results">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32" className="no-results-icon">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <p>No units match your current filters.</p>
                {onClearFilters && (
                    <button className="no-results-clear" onClick={onClearFilters}>Clear all filters</button>
                )}
            </div>
        );
    }

    return (
        <div className="table-wrapper">
            <div className="table-container">
                <table className="inventory-table">
                    <thead>
                        <tr>
                            <th className="col-status"></th>
                            <th {...sortableTh('StockNumber')}>Stock # {getSortIndicator('StockNumber')}</th>
                            <th {...sortableTh('MAKE')}>Equipment {getSortIndicator('MAKE')}</th>
                            <th {...sortableTh('BranchName')}>Branch {getSortIndicator('BranchName')}</th>
                            <th {...sortableTh('DaysInStock')} className="text-center">Days {getSortIndicator('DaysInStock')}</th>
                            <th {...sortableTh('Best_Sell_Price')} className="text-right">Price {getSortIndicator('Best_Sell_Price')}</th>
                            <th {...sortableTh(AI_GAP_SORT_KEY, 'desc')} className="text-right col-ai-price">
                                <span className="ai-price-marker" aria-hidden="true"></span>
                                AI Price
                                <AiPriceHeaderTip />
                                {getSortIndicator(AI_GAP_SORT_KEY)}
                            </th>
                            <th {...sortableTh('CarryingCostBasis')} className="text-right">
                                True Cost
                                <InfoTip text="Sum of all cost fields for In-Stock units (wholesale + retail + access + after-market + charge + option + other + paint + pre-delivery + repair + surcharge + trim). For Pending units, this is Cost_Estimate (Sales Book Value + planned reconditioning)." />
                                {getSortIndicator('CarryingCostBasis')}
                            </th>
                            <th {...sortableTh('LadenhueterRisk', 'desc')} className="text-center">
                                Risk
                                <InfoTip text="Sell-Speed Risk — calibrated slow-mover (Ladenhüter) risk, 0–100%. Red = high risk of slow sale, Yellow = medium, Green = low. Sort to surface the riskiest units first. Blank = not yet scored." />
                                {getSortIndicator('LadenhueterRisk')}
                            </th>
                            <th {...sortableTh('QuoteCount')} className="text-center">
                                Interest
                                <InfoTip text="Buyer interest based on Salesforce JDQuote activity. Hot = quoted in last 7 days. Warm = last 30 days. Cold = last 90 days. No Interest = never quoted or over 90 days ago. Number shown = total quote count." />
                                {getSortIndicator('QuoteCount')}
                            </th>
                            <th {...sortableTh('LastQuotedGapPct')} className="text-center">
                                Last Quote
                                <InfoTip text="Gap between the most recent active per-machine quoted price (JDQuoteEquipment) and the current asking price. Negative = buyer offered below ask. Positive = buyer offered above. Within −10% is a retarget signal — call before dropping the listed price." />
                                {getSortIndicator('LastQuotedGapPct')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((unit) => {
                            return (
                                <tr key={unit.StockNumber} onClick={() => onUnitClick(unit, 'ai')}>
                                    <td className="col-status">
                                        <span className="status-dot" style={{ backgroundColor: getAgingStatusColor(unitAgingBucketId(unit, aging)) }} title={unitAgingLabel(unit, aging)}></span>
                                    </td>
                                    <td className="font-medium clickable-cell" onClick={(e) => { e.stopPropagation(); onUnitClick(unit, 'overview'); }}>{unit.StockNumber}</td>
                                    <td className="col-equipment">
                                        <div className="equipment-cell">
                                            <span className="equipment-title">
                                                <span className="equipment-name" title={`${unit.MAKE ?? ''} ${unit.MODEL ?? ''}`.trim()}>{unit.MAKE} {unit.MODEL}</span>
                                                {unit.Model_Year && <span className="equipment-year">{unit.Model_Year}</span>}
                                            </span>
                                            {unit.AnomalyVerdict && unit.AnomalySeverity !== 'normal' && (() => {
                                                const s = getAnomalyStyle(unit.AnomalySeverity);
                                                return s ? (
                                                    <span
                                                        className="anomaly-row-badge"
                                                        style={{ backgroundColor: s.bg, color: s.text, borderColor: s.border }}
                                                        title={anomalyReasonText(unit) || unit.AnomalyVerdict}
                                                    >
                                                        {unit.AnomalyVerdict}
                                                    </span>
                                                ) : null;
                                            })()}
                                            {unit.HasQualifyingQuote && (
                                                <span
                                                    className="anomaly-row-badge qualifying-quote-badge"
                                                    title={`Open quote at $${unit.MaxOpenQuoteTotal?.toLocaleString('en-US')} meets or exceeds AI estimated price — retarget this customer`}
                                                >
                                                    <svg viewBox="0 0 20 20" fill="currentColor" width="9" height="9" style={{ marginRight: 3 }}>
                                                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                                    </svg>
                                                    Live Quote
                                                </span>
                                            )}
                                            {retargetActive && (() => {
                                                const viaGap = unit.LastQuotedGapPct != null && unit.LastQuotedGapPct >= -10;
                                                const viaAI  = unit.HasQualifyingQuote;
                                                if (!viaGap && !viaAI) return null;
                                                const label = viaGap && viaAI
                                                    ? 'Gap ≤ 10% · ≥ AI Price'
                                                    : viaGap ? 'Gap ≤ 10% of ask' : '≥ AI Price';
                                                const tip = viaGap && viaAI
                                                    ? `Qualifies on both: last quote within 10% of asking price (${unit.LastQuotedGapPct?.toFixed(1)}%) AND at or above AI estimated value`
                                                    : viaGap
                                                        ? `Last quoted price is within 10% of asking price (${unit.LastQuotedGapPct?.toFixed(1)}%) — call before dropping list price`
                                                        : `Buyer's active quote meets or exceeds AI estimated value — asking price may be high but buyer is offering fair market value`;
                                                return (
                                                    <span
                                                        className="anomaly-row-badge"
                                                        style={{ backgroundColor: '#0f766e15', color: '#0f766e', borderColor: '#0f766e30' }}
                                                        title={tip}
                                                    >
                                                        Retarget: {label}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                    <td>{unit.BranchName ?? unit.LOCATION ?? '—'}</td>
                                    <td className="text-center clickable-cell" onClick={(e) => { e.stopPropagation(); onUnitClick(unit, 'overview'); }}>
                                        <span className="days-badge" style={{
                                            backgroundColor: getAgingStatusColor(unitAgingBucketId(unit, aging)) + '15',
                                            color: getAgingStatusColor(unitAgingBucketId(unit, aging)),
                                        }}>
                                            {unit.DaysInStock?.toLocaleString('en-US')}
                                        </span>
                                    </td>
                                    <td className="text-right font-medium clickable-cell" onClick={(e) => { e.stopPropagation(); onUnitClick(unit, 'ai'); }}>
                                        <div className="price-cell">
                                            {unit.Best_Sell_Price > 0
                                                ? <span>{formatCurrency(unit.Best_Sell_Price)}</span>
                                                : <span className="text-muted">—</span>}
                                        </div>
                                    </td>
                                    <td className="text-right clickable-cell col-ai-price" onClick={(e) => { e.stopPropagation(); onUnitClick(unit, 'ai'); }}>
                                        <AiPriceCell unit={unit} />
                                    </td>
                                    <td className="text-right clickable-cell" onClick={(e) => { e.stopPropagation(); onUnitClick(unit, 'pricing'); }}>
                                        {unit.CarryingCostBasis != null ? (
                                            <span className="true-cost-value">{formatCurrency(unit.CarryingCostBasis)}</span>
                                        ) : (
                                            <span className="true-cost-empty">—</span>
                                        )}
                                    </td>
                                    <td className="text-center clickable-cell" onClick={(e) => { e.stopPropagation(); onUnitClick(unit, 'ai'); }}>
                                        {unit.RiskTier ? (() => {
                                            const r = getRiskTierStyle(unit.RiskTier);
                                            return (
                                                <span
                                                    className="risk-cell"
                                                    style={{ color: r.text }}
                                                    title={`Sell-Speed Risk: ${r.label} (${unit.RiskTier})${unit.LadenhueterRisk != null ? ` — ${unit.LadenhueterRisk.toFixed(0)}% slow-mover risk` : ''}`}
                                                >
                                                    <span className="risk-dot" style={{ backgroundColor: r.dot }}></span>
                                                    {unit.LadenhueterRisk != null && (
                                                        <span className="risk-pct">{unit.LadenhueterRisk.toFixed(0)}%</span>
                                                    )}
                                                    <span className="risk-label">{r.label}</span>
                                                </span>
                                            );
                                        })() : (
                                            <span className="text-muted">–</span>
                                        )}
                                    </td>
                                    <td className="text-center clickable-cell" onClick={(e) => { e.stopPropagation(); onUnitClick(unit, 'pipeline'); }}>
                                        {(() => {
                                            const heat = getBuyerHeatStatus(unit.QuoteCount, unit.DaysSinceLastQuote);
                                            const hasQuotes = unit.QuoteCount > 0;
                                            const hasTHLeads = unit.THEmailLeads30d != null && unit.THEmailLeads30d > 0;
                                            const sfTooltip = {
                                                'Hot':         'Hot — quoted within last 7 days',
                                                'Warm':        'Warm — quoted within last 30 days',
                                                'Cold':        'Cold — quoted within last 90 days',
                                                'No Interest': 'No Interest — never quoted or >90 days ago',
                                            };
                                            const tooltipText = hasTHLeads
                                                ? `${sfTooltip[heat.label]} · ${unit.THEmailLeads30d} marketplace email lead${unit.THEmailLeads30d === 1 ? '' : 's'} (last 30d)`
                                                : sfTooltip[heat.label];
                                            return (
                                                <span className="heat-tooltip-wrap">
                                                    <span
                                                        className="heat-badge"
                                                        style={{ color: heat.color, backgroundColor: heat.bg }}
                                                    >
                                                        <span className="heat-dot" style={{ backgroundColor: heat.color }}></span>
                                                        {heat.label}
                                                        {hasQuotes && (
                                                            <span className="heat-count">{unit.QuoteCount}</span>
                                                        )}
                                                        {hasTHLeads && (
                                                            <span className="th-lead-dot" title={`${unit.THEmailLeads30d} marketplace email lead${unit.THEmailLeads30d === 1 ? '' : 's'}`}></span>
                                                        )}
                                                    </span>
                                                    <span className="heat-tooltip">{tooltipText}</span>
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="text-center clickable-cell" onClick={(e) => { e.stopPropagation(); onUnitClick(unit, 'pipeline'); }}>
                                        {(() => {
                                            const gap = unit.LastQuotedGapPct;
                                            if (gap == null) return <span className="text-muted">—</span>;
                                            const abs = Math.abs(gap);
                                            let color, bg;
                                            if (gap >= 0) {
                                                color = '#15803d'; bg = '#15803d15';
                                            } else if (abs <= 5) {
                                                color = '#1d4ed8'; bg = '#1d4ed815';
                                            } else if (abs <= 10) {
                                                color = '#b45309'; bg = '#b4530915';
                                            } else {
                                                color = '#b91c1c'; bg = '#b91c1c15';
                                            }
                                            return (
                                                <span
                                                    className="last-quote-badge"
                                                    style={{ color, backgroundColor: bg }}
                                                    title={`Last per-machine quoted price was ${gap > 0 ? '+' : ''}${gap.toFixed(1)}% vs asking price`}
                                                >
                                                    {gap > 0 ? '+' : ''}{gap.toFixed(1)}%
                                                </span>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="table-pagination">
                    <div className="pagination-info">
                        Showing <strong>{((page - 1) * pageSize + 1).toLocaleString('en-US')}</strong>–<strong>{Math.min(page * pageSize, sortedData.length).toLocaleString('en-US')}</strong> of <strong>{sortedData.length.toLocaleString('en-US')}</strong>
                    </div>
                    <div className="pagination-controls">
                        <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} aria-label="Previous page">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        </button>
                        {getPageNumbers().map((p, i) =>
                            p === '...' ? (
                                <span key={`dots-${i}`} className="page-ellipsis">&hellip;</span>
                            ) : (
                                <button key={p} className={`page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                            )
                        )}
                        <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} aria-label="Next page">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                        </button>
                    </div>
                    <div className="pagination-size">
                        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} aria-label="Rows per page">
                            <option value={25}>25 / page</option>
                            <option value={50}>50 / page</option>
                            <option value={100}>100 / page</option>
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
}

InventoryTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    onUnitClick: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    onClearFilters: PropTypes.func,
    retargetActive: PropTypes.bool,
};

InventoryTable.defaultProps = {
    loading: false,
    onClearFilters: null,
};

export default InventoryTable;
